import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import TimeMachineIcon from '../../assets/icons/TimeMachineIcon';
import { trackSearch } from '../../services/analytics';
import './SearchDropdown.css';

const SearchDropdown = ({ onClose }) => {
    const [query, setQuery] = useState('');
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchHistory, setSearchHistory] = useState([]);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        inputRef.current?.focus();
        loadSearchHistory();
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    useEffect(() => {
        if (query.trim().length > 0) {
            searchUsers();
        } else {
            setUsers([]);
        }
    }, [query]);

    const loadSearchHistory = () => {
        const history = JSON.parse(localStorage.getItem('searchHistory') || '[]');
        setSearchHistory(history.slice(0, 3));
    };

    const saveToHistory = (searchQuery) => {
        const history = JSON.parse(localStorage.getItem('searchHistory') || '[]');
        const filtered = history.filter(q => q !== searchQuery);
        const newHistory = [searchQuery, ...filtered].slice(0, 3);
        localStorage.setItem('searchHistory', JSON.stringify(newHistory));
    };

    const searchUsers = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/search/users?q=${encodeURIComponent(query)}`);
            setUsers(response.data);
        } catch (error) {
            console.error('Failed to search users:', error);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleShowAllResults = () => {
        if (query.trim()) {
            saveToHistory(query.trim());
            trackSearch(query.trim());
            navigate(`/search?q=${encodeURIComponent(query.trim())}`);
            onClose();
        }
    };

    const handleHistoryClick = (historyQuery) => {
        setQuery(historyQuery);
        navigate(`/search?q=${encodeURIComponent(historyQuery)}`);
        onClose();
    };

    const handleUserClick = (userId) => {
        navigate(`/user/${userId}`);
        onClose();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && query.trim()) {
            handleShowAllResults();
        }
    };

    return (
        <div className="search-dropdown" ref={dropdownRef}>
            <div className="search-dropdown__input-wrapper">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM19 19l-4.35-4.35"
                          stroke="#666"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"/>
                </svg>
                <input
                    ref={inputRef}
                    type="text"
                    className="search-dropdown__input"
                    placeholder="Поиск пользователей и постов..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                />
            </div>

            <div className="search-dropdown__content">
                {query.trim().length === 0 && searchHistory.length > 0 && (
                    <div className="search-dropdown__section">
                        <div className="search-dropdown__section-title">История поиска</div>
                        {searchHistory.map((item, idx) => (
                            <div
                                key={idx}
                                className="search-dropdown__history-item"
                                onClick={() => handleHistoryClick(item)}
                            >
                                <TimeMachineIcon size={16} className="search-dropdown__history-icon" />
                                <span>{item}</span>
                            </div>
                        ))}
                    </div>
                )}

                {query.trim().length > 0 && (
                    <>
                        {loading ? (
                            <div className="search-dropdown__loading">Поиск...</div>
                        ) : users.length > 0 ? (
                            <div className="search-dropdown__section">
                                <div className="search-dropdown__section-title">Пользователи</div>
                                {users.map(user => (
                                    <div
                                        key={user.id}
                                        className="search-dropdown__user-item"
                                        onClick={() => handleUserClick(user.id)}
                                    >
                                        {user.avatar ? (
                                            <img
                                                src={user.avatar}
                                                alt={user.username}
                                                className="search-dropdown__user-avatar"
                                            />
                                        ) : (
                                            <div className="search-dropdown__user-avatar">
                                                {user.username.charAt(0).toUpperCase()}
                                            </div>
                                        )}
                                        <div className="search-dropdown__user-info">
                                            <div className="search-dropdown__user-name">
                                                {user.displayName || user.username}
                                            </div>
                                            <div className="search-dropdown__user-meta">
                                                @{user.username} · {user.subscribersCount} подписчиков
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="search-dropdown__empty">Пользователи не найдены</div>
                        )}

                        <button
                            className="search-dropdown__show-all"
                            onClick={handleShowAllResults}
                        >
                            Показать все результаты
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default SearchDropdown;